import LoadEquipmentInfo from "../equipment-list/LoadEquipmentInfo";
import BookingEquipmentListTab from "../equipment-list/BookingEquipmentListTab";
import CalendarMainPage from "./calendar-timeline/CalendarMainPage";
import ForWhomMainPage from "./whom/ForWhomMainPage";
import { useContext, useEffect, useState, useRef } from "react";
import { formatUserDisplay, isSameDayBooking } from "../../utils/helpers/text/index";
import {
  CHANGE_EXPIREMENT_TEXT,
  emptyBookingDetails,
  equipmentTabGroup,
  TIMELINE_TYPE,
  DEFINE_EXPIREMENT_TEXT,
  TIMELINE_CONFIG,
} from "../../constants";
import { Formik, useFormikContext } from "formik";
import { ForWhomMainPageSchema } from "./validations/ForWhomMainPageSchema";
import BookEquipmentAction from "./BookEquipmentAction";
import moment from "moment/moment";
import { connect, useSelector } from "react-redux";
import { BookingContext } from "../booking/context";
import { RecurringBookingProvider } from "./calendar-timeline/RecurringBooking/Context/RecurringBookingContext";
import { CustomFilterContext } from "../booking-instruments/booking-instruments-custom-filter/context";
import { loadBookingInstruments as loadBookingInstrumentsAction } from "../booking-instruments/redux/actions";
import omit from "lodash/omit";
import isEmpty from "lodash/isEmpty";
import { OwcButton } from "@one/react";
import { isAddBookingDisabled, isSaveBookingDisabled } from "./helpers/bookingValidation";
import ConfirmDialog from "../../components/shared/ConfirmDialog";

// Inner component to access formik context and render pagination buttons
const PaginationButtons = ({ activeTab, disabledSelection, handlersRef }) => {
  const formik = useFormikContext();
  const { pendingReservations } = useSelector((state) => state.reservations);
  const { isEditMode, isDuplicate } = useContext(BookingContext);

  // Only show on tab 1 (equipment selection)
  if (activeTab !== 1) {
    return null;
  }

  // Use handlers from ref if available, otherwise use empty functions
  const handlers = handlersRef?.current || {
    handlerOnAddBooking: () => { },
    handleOpen: () => { },
    compareValues: () => true
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {(!isEditMode || isDuplicate) && (
        <OwcButton
          style={{ float: "right", marginLeft: 'auto' }}
          data-testid="whom-tab-confrim-button"
          variant="primary"
          onClick={handlers.handlerOnAddBooking}
          disabled={isAddBookingDisabled(formik, isDuplicate, pendingReservations, disabledSelection)}
        >
          Confirm
        </OwcButton>
      )}
      {(isEditMode && !isDuplicate) && (
        <OwcButton
          style={{ float: "right", marginLeft: 'auto' }}
          data-testid="whom-tab-confrim-button"
          variant="primary"
          onClick={handlers.handleOpen}
          disabled={isSaveBookingDisabled(formik, disabledSelection, pendingReservations, handlers.compareValues)}
        >
          Save
        </OwcButton>
      )}
    </div>
  );
};

const BookingTabContainer = ({ setSubscriptionStatus, setPopupEnableStatus, loadBookingInstruments, landingPageAppSyncClient }) => {
  const user = useSelector((store) => store.user);
  const isPredUser = user.isPredUser;
  const bookingInstruments = useSelector((store) => store.bookingInstruments);
  const { selectedList } = useContext(CustomFilterContext);
  const { isEditMode, editBookingData, isCreateMode, dispatchAction } = useContext(BookingContext);

  const [isDateSame] = useState(() =>
    isEditMode ? isSameDayBooking(editBookingData?.dateFrom, editBookingData?.dateTo, user.timezone) : false
  );
  const [activeTab, setActiveTab] = useState(() => {
    // If create-from-equipment flow set startAtStep = 3, jump straight to step 3
    if (isCreateMode && (editBookingData?.startAtStep === 3)) return 3;
    return isEditMode ? 2 : 1;
  });
  const [tabList, setTabList] = useState(() => {
    const tempTabList = equipmentTabGroup ? [...equipmentTabGroup] : [];
    if (isEditMode && tempTabList.length > 1) {
      tempTabList[1].content = CHANGE_EXPIREMENT_TEXT;
      if (editBookingData?.bookedEquipments?.length > 0) {
        tempTabList[1].isDisable = false
      }
    }
    return tempTabList;
  });

  // If landing with startAtStep=3, ensure tab states reflect that
  useEffect(() => {
    if (editBookingData?.startAtStep === 3 && tabList?.length >= 3) {
      const updated = tabList.map(t => ({ ...t, isActive: t.id === 3 }));
      setTabList(updated);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [selectedStartDate, setSelectedStartDate] = useState(isEditMode ? editBookingData?.dateFrom : null);
  const [selectedEndDate, setSelectedEndDate] = useState(isEditMode ? editBookingData?.dateTo : null);
  const [disabledSelection, setDisableSelection] = useState(true);
  const [equipmentDetailList, setEquipmentDetailList] = useState(() => {
    const entries = isEditMode && editBookingData?.bookingEntries ? editBookingData.bookingEntries : [];
    return entries.map((x) => {
      const eq = x.equipment || {};
      return {
        ...eq,
        id: x.inventoryId,
        shortIdentifier: eq.shortIdentifier || eq.equipmentModel || x.inventoryId,
        equipmentModel: eq.equipmentModel || eq.shortIdentifier || x.inventoryId,
      };
    });
  });
  const [view, setView] = useState(
    isEditMode ? (isDateSame ? TIMELINE_TYPE.DAY : TIMELINE_TYPE.WEEK) : TIMELINE_TYPE.DAY
  );
  const [isStepEnable, setIsStepEnable] = useState(false);
  const [newEquipmentIds, setNewEquipmentIds] = useState([]);
  const [calView, setCalView] = useState(isEditMode ? (isDateSame ? TIMELINE_CONFIG.DAY : TIMELINE_CONFIG.WEEK) :
    TIMELINE_CONFIG.DAY);
  const [cancelStatus, setCancelStatus] = useState(false);
  const [deleteResourceId, setDeleteResourceId] = useState(null);
  const [limitExceedStatus, setLimitExceedStatus] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);

  // Ref to hold handlers from BookEquipmentAction
  const handlersRef = useRef(null);

  // Browser back button handling for steps 2 and 3
  useEffect(() => {
    if (activeTab === 1) {
      return;
    }

    // Add fake history entry to create a buffer
    window.history.pushState(null, '', window.location.href);

    const handlePopState = () => {
      // Prevent navigation by adding another pushState
      window.history.pushState(null, '', window.location.href);
      // Show exit confirmation modal
      setShowExitModal(true);
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [activeTab]);

  // Handle confirmed exit - navigate back and force re-render
  const handleConfirmExit = () => {
    setShowExitModal(false);
    setPopupEnableStatus(false);
    // Reset booking context (disable isEditMode and clear editBookingData)
    dispatchAction({ type: "resetBookingData" });
    // Go back 2 steps to bypass the fake entry
    window.history.go(-2);
  };

  const handleCancelExit = () => {
    setShowExitModal(false);
  };

  useEffect(() => {
    if (selectedEndDate && !isStepEnable) {
      setIsStepEnable(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEndDate]);
  useEffect(() => {
    updateEquipmentDetailsInRedux();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedList, equipmentDetailList]);
  const handleTabChange = (id) => {
    if (!tabList || !Array.isArray(tabList)) {
      return;
    }
    const tempData = tabList.map((subItem) => {
      subItem.isActive = false;
      if (subItem.id === id) {
        subItem.isActive = true;
      }
      return subItem;
    });
    if (id === 3 && tempData.length > 1) {
      tempData[1].content = `${moment(selectedStartDate).tz(user.timezone).format("DD-MMM-YYYY")} to ${moment(
        selectedEndDate
      )
        .tz(user.timezone)
        .format("DD-MMM-YYYY")}`;
    } else if (tabList.length > 1) {
      tabList[1].content = selectedEndDate ? CHANGE_EXPIREMENT_TEXT : DEFINE_EXPIREMENT_TEXT;
    }
    setPopupEnableStatus(id === 2 || id === 3);
    setActiveTab(id);
    setTabList(tempData);
  };
  const getEquipmentDetailsList = (inventoryIdList, detailList) => {
    const bookingInstruments = [];

    inventoryIdList.forEach((x) => {
      const findRow = detailList.find((y) => y.id === x);
      if (findRow !== undefined) {
        bookingInstruments.push({
          ...findRow,
          inventoryId: findRow.id,
          shortIdentifier: findRow?.shortIdentifier ?? (
            findRow?.equipmentModel && findRow?.serialNumber
              ? `${findRow.equipmentModel} - ${findRow.serialNumber}`
              : (findRow?.equipmentModel || findRow?.shortIdentifier)
          ),
        });
      }
    });

    // Sort by shortIdentifier in alphabetical order
    bookingInstruments.sort((a, b) => {
      const aIdentifier = a.shortIdentifier || '';
      const bIdentifier = b.shortIdentifier || '';
      return aIdentifier.localeCompare(bIdentifier);
    });

    return bookingInstruments;
  };

  // Auto-advance to step 3 in create mode when all necessary data is available
  useEffect(() => {
    if (isCreateMode &&
      selectedEndDate &&
      equipmentDetailList.length > 0 &&
      activeTab < 3) {
      // Move to step 3 automatically when we have equipment and dates selected
      handleTabChange(3);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreateMode]);

  let bookedUser = [];
  if (isEditMode && editBookingData?.bookedUsers) {
    bookedUser = (editBookingData.bookedUsers || []).filter(Boolean).map((item) => ({
      ...item,
      key: item.email,
      userId: item.user || item.userId,
      value: formatUserDisplay(item),
    }));
  }

  const getActiveStep = () => {
    switch (activeTab) {
      case 1:
        return (
          <LoadEquipmentInfo
            isStepEnable={isStepEnable}
            setNewEquipmentIds={setNewEquipmentIds}
            setEquipmentDetailList={setEquipmentDetailList}
            setLimitExceedStatus={setLimitExceedStatus}
            limitExceedStatus={limitExceedStatus}
            landingPageAppSyncClient={landingPageAppSyncClient}
            paginationActions={
              <PaginationButtons
                activeTab={activeTab}
                disabledSelection={disabledSelection}
                handlersRef={handlersRef}
              />
            }
          />
        );
      case 2:
        return (
          <CalendarMainPage
            equipmentDetailList={equipmentDetailList}
            getEquipmentDetailsList={getEquipmentDetailsList}
            selectedStartDate={selectedStartDate}
            setSelectedStartDate={setSelectedStartDate}
            selectedEndDate={selectedEndDate}
            setSelectedEndDate={setSelectedEndDate}
            tabList={tabList}
            setTabList={setTabList}
            view={view}
            setView={setView}
            calView={calView}
            setCalView={setCalView}
            newEquipmentIds={newEquipmentIds}
            setIsStepEnable={setIsStepEnable}
            setNewEquipmentIds={setNewEquipmentIds}
            isEditMode={isEditMode}
            cancelStatus={cancelStatus}
            setCancelStatus={setCancelStatus}
            deleteResourceId={deleteResourceId}
            setDeleteResourceId={setDeleteResourceId}
          />
        );
      case 3:
        return (
          <ForWhomMainPage
            cancelStatus={cancelStatus}
            setCancelStatus={setCancelStatus}
            deleteResourceId={deleteResourceId}
            setDeleteResourceId={setDeleteResourceId}
          />
        );
      default:
        return <LoadEquipmentInfo setEquipmentDetailList={setEquipmentDetailList} />;
    }
  };
  const updateEquipmentDetailsInRedux = () => {
    const bookingInstruments = getEquipmentDetailsList(selectedList, equipmentDetailList);
    loadBookingInstruments({
      bookingInstruments: bookingInstruments,
    });
  };

  return (
    <>
      <RecurringBookingProvider>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            padding: activeTab === 2 ? "16px 16px 0px" : "16px 16px 16px 16px",
            width: "100%",
          }}
        >
          <BookingEquipmentListTab
            tabList={tabList}
            handleTabChange={handleTabChange}
            setTabList={setTabList}
            selectedStartDate={selectedStartDate}
            selectedEndDate={selectedEndDate}
            setDisableSelection={setDisableSelection}
            activeTab={activeTab}
            setSelectedStartDate={setSelectedStartDate}
            setSelectedEndDate={setSelectedEndDate}
            equipmentDetailList={equipmentDetailList}
            setLimitExceedStatus={setLimitExceedStatus}
          />
        </div>
        <Formik
          validationSchema={ForWhomMainPageSchema}
          initialValues={

          isEditMode && !isCreateMode
            ? (() => {
              let { assistanceDeclaration, hazardousDeclaration, bookingApprovalInfo } = editBookingData
              if ((!assistanceDeclaration || !hazardousDeclaration) && bookingApprovalInfo) {
                assistanceDeclaration = bookingApprovalInfo.assistanceDeclaration;
                hazardousDeclaration = bookingApprovalInfo.hazardousDeclaration;
              }
              return {
                bookingType: editBookingData?.type,
                bookingFor: bookedUser,
                project: editBookingData?.project,
                redPandaProjects: editBookingData?.redPandaProjects
                  ? editBookingData.redPandaProjects.map(({ __typename, ...rest }) => rest)
                  : editBookingData?.redPandaProjects,
                description: editBookingData?.description,
                hasHazardousMaterials: hazardousDeclaration?.hasHazardousMaterials,
                hazardousMaterialsText: hazardousDeclaration?.hazardousMaterialsList || "",
                elnId: isEmpty(omit(editBookingData?.elnId, "__typename")) ? null : omit(editBookingData?.elnId, "__typename"),
                withAssistance: assistanceDeclaration?.requiresAssistance,
                assistanceReason: assistanceDeclaration?.assistanceReason || "",
                isPredUser,
              }
            })()
            : (() => {
              // added assistedBooking because of comming
              const assistedBooking = bookingInstruments?.bookingInstruments?.some(equipment => !!+equipment?.shouldAssisted || !!+equipment?.assistedBooking);
              const hazardousBooking = bookingInstruments?.bookingInstruments?.some(equipment => !!+equipment?.isHazardous);
              return {
                ...emptyBookingDetails,
                ...(assistedBooking ? {withAssistance: false, assistanceReason: ""} : {}),
                ...(hazardousBooking ? {hasHazardousMaterials: true, hazardousMaterialsText: ""} : {}),
                isPredUser,
              };
            })()
        }
        validateOnMount={true}
        enableReinitialize={true}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
          }}
        >
          {getActiveStep()}
          <BookEquipmentAction
            disabledSelection={disabledSelection}
            handleTabChange={handleTabChange}
            tabList={tabList}
            setTabList={setTabList}
            setSelectedStartDate={setSelectedStartDate}
            setSelectedEndDate={setSelectedEndDate}
            setSubscriptionStatus={setSubscriptionStatus}
            selectedStartDate={selectedStartDate}
            selectedEndDate={selectedEndDate}
            setPopupEnableStatus={setPopupEnableStatus}
            activeTab={activeTab}
            handlersRef={handlersRef}
          />
        </div>
      </Formik>
      </RecurringBookingProvider>
      <ConfirmDialog
        approveText="Leave page"
        approveColor="primary"
        approveVariant="contained"
        cancelText="Cancel"
        cancelVariant="outlined"
        cancelColor="primary"
        onApprove={handleConfirmExit}
        title="Leave page"
        content="The changes that you made won't be saved!"
        disableBooking={false}
        close={handleCancelExit}
        open={showExitModal}
        onCancel={handleCancelExit}
        fullWidth={false}
        maxWidth="sm"
      />
    </>
  );
};

export default connect(null, {
  loadBookingInstruments: loadBookingInstrumentsAction,
})(BookingTabContainer);
